import {useEffect, useSyncExternalStore} from "react";
import store from "@/store";
import {ScoutingPipelineAPI} from "@/api/scouting_pipeline";
import {
	getDsContext,
	isDsContextReady,
	onDsContextChange,
} from "@/utils/dsContext";

// Colori come valori comuni (var): seguono il tema; per la stampa printColor in style/utils
export const PIPELINE_STAGES = [
	{
		id: "watching",
		label: "Monitorato",
		color: "var(--color-text-secondary)",
		border: "var(--color-border)",
		bg: "var(--color-surface-soft)",
		dot: "var(--color-text-muted)",
	},
	{
		id: "evaluating",
		label: "In valutazione",
		color: "var(--color-primary)",
		border: "var(--color-primary-alpha-25)",
		bg: "var(--color-primary-alpha-8)",
		dot: "var(--color-primary)",
	},
	{
		id: "target",
		label: "Obiettivo",
		color: "var(--color-warning)",
		border: "var(--color-warning-bg)",
		bg: "var(--color-warning-bg)",
		dot: "var(--color-warning)",
	},
	{
		id: "negotiating",
		label: "In trattativa",
		color: "var(--color-good)",
		border: "var(--color-good-bg)",
		bg: "var(--color-good-bg)",
		dot: "var(--color-good)",
	},
];

export const getPipelineStage = (status) =>
	PIPELINE_STAGES.find((s) => s.id === status) || null;

let state = {data: {}, status: "idle", error: null, busy: false};
const listeners = new Set();
let request = null;
let generation = 0;
const publish = (changes) => {
	state = {...state, ...changes};
	listeners.forEach((listener) => listener());
};
onDsContextChange(() => {
	generation += 1;
	request = null;
	publish({data: {}, status: "idle", error: null, busy: false});
});

export const pipelineAccess = () => {
	const user = store.getState().user;
	const permission = user?.areaPermissions?.find((p) => p.area === "Scouting");
	const admin = user?.roleName?.toLowerCase() === "admin";
	return {
		canRead: admin || !!permission,
		canWrite: admin || permission?.accessLevel === "FullControl",
	};
};

// Fotografia in memoria, sempre svuotata al cambio di sessione o stagione.
export const loadPipeline = () => {
	getDsContext();
	return state.data;
};
export const getPipelineEntry = (id) => loadPipeline()[id] || null;
export const refreshPipeline = (force = false) => {
	const context = getDsContext();
	if (!isDsContextReady())
		return Promise.reject(new Error("Sessione scouting non disponibile"));
	if (request && !force) return request;
	const ticket = ++generation;
	publish({status: "loading", error: null});
	const pending = ScoutingPipelineAPI.list(store.getState().season?.id)
		.then((rows) => {
			if (context !== getDsContext() || ticket !== generation)
				throw new Error("Richiesta scouting superata");
			const data = Object.fromEntries(
				(rows || []).map((entry) => [entry.playerId, entry]),
			);
			publish({data, status: "ready", error: null});
			return data;
		})
		.catch((error) => {
			if (context === getDsContext() && ticket === generation)
				publish({data: {}, status: "error", error});
			throw error;
		})
		.finally(() => {
			if (request === pending) request = null;
		});
	request = pending;
	return pending;
};

const mutate = async (operation) => {
	const context = getDsContext();
	if (!pipelineAccess().canWrite)
		throw new Error("Non sei abilitato a modificare la pipeline");
	if (state.busy || state.status !== "ready")
		throw new Error("Attendi il caricamento della pipeline e riprova");
	publish({busy: true});
	generation += 1;
	request = null;
	try {
		await operation();
		if (context !== getDsContext())
			throw new Error("Sessione scouting cambiata");
		return await refreshPipeline(true);
	} finally {
		if (context === getDsContext()) publish({busy: false});
	}
};

export const putPipelineEntry = (id, data) =>
	mutate(() => ScoutingPipelineAPI.put(id, data));
export const removePipelineEntry = (id) =>
	mutate(() => ScoutingPipelineAPI.remove(id));
export const toggleMonitored = async (id) => {
	const entry = getPipelineEntry(id);
	if (entry && entry.status !== "watching") return entry;
	if (entry) await removePipelineEntry(id);
	else await putPipelineEntry(id, {status: "watching", note: ""});
	return getPipelineEntry(id);
};

export const useScoutingPipeline = ({enabled = true} = {}) => {
	const context = getDsContext();
	const current = useSyncExternalStore(
		(listener) => {
			listeners.add(listener);
			return () => listeners.delete(listener);
		},
		() => state,
	);
	useEffect(() => {
		if (!enabled || !isDsContextReady()) return;
		const reload = () => {
			if (!document.hidden) refreshPipeline().catch(() => {});
		};
		reload();
		window.addEventListener("focus", reload);
		const timer = setInterval(reload, 30000);
		return () => {
			window.removeEventListener("focus", reload);
			clearInterval(timer);
		};
	}, [context, enabled]);
	return {
		...current,
		pipeline: current.data,
		loading: current.status === "idle" || current.status === "loading",
		canWrite:
			pipelineAccess().canWrite && current.status === "ready" && !current.busy,
		refresh: () => refreshPipeline(true),
	};
};

export const isMonitored = (entry) => entry?.status === "watching";

// Monitorati senza squadra nota per la stagione richiesta.
export const monitoredWithoutRoster = (pipeline, seasonId) =>
	Object.values(pipeline || {})
		.filter(isMonitored)
		.filter(
			(e) => !e.rosterId || (e.rosterSeasonId && e.rosterSeasonId !== seasonId),
		)
		.map((e) => e.playerId);

// Numero di monitorati per squadra, solo per la stagione richiesta.
export const monitoredRosterCounts = (pipeline, seasonId) => {
	const counts = new Map();
	Object.values(pipeline || {})
		.filter(isMonitored)
		.filter(
			(e) => e.rosterId && (!e.rosterSeasonId || e.rosterSeasonId === seasonId),
		)
		.forEach((e) => counts.set(e.rosterId, (counts.get(e.rosterId) || 0) + 1));
	return counts;
};

export const monitoredInMatch = (match, counts) =>
	(counts.get(match?.homeRosterId) || 0) +
	(match?.awayRosterId !== match?.homeRosterId
		? counts.get(match?.awayRosterId) || 0
		: 0);
